import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Shield, CheckCircle2, ArrowRight, MessageCircle } from 'lucide-react';
import ChatPopup from '../components/ChatPopup';
import RegistrationPopup from '../components/RegistrationPopup';
import PricingSection from '../components/PricingSection';

const TrademarkRegistration = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [tmSignUpType, setTmSignUpType] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState({ title: 'Basic', amount: 45 });
  const benefits = [
    'Exclusive rights to use your mark nationwide',
    'Legal presumption of ownership',
    'Ability to sue in federal court',
    'Use of ® registered symbol',
    'Deterrent to potential infringers',
    'Foundation for international registration',
    'Valuable business asset',
    'Protection against domain name hijacking'
  ];

  const faqs = [
    {
      question: 'What can be trademarked?',
      answer: 'You can trademark names, logos, slogans, sounds, colors, and even product shapes that distinguish your goods or services from others in the marketplace.'
    },
    {
      question: 'How long does registration take?',
      answer: 'The typical trademark registration process takes 8-12 months from filing to approval, though this can vary based on application complexity and any office actions.'
    },
    {
      question: 'Do I need a trademark attorney?',
      answer: 'While not legally required, working with an experienced trademark attorney significantly increases your chances of approval and ensures proper protection of your brand.'
    },
    {
      question: 'What is the difference between TM and ®?',
      answer: 'TM indicates you are claiming rights to a mark, while ® indicates your mark is officially registered with the USPTO and provides enhanced legal protection.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto">
                <Shield className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Trademark Registration Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Protect your brand identity with professional trademark registration. Secure nationwide exclusive rights to your business name, logo, or slogan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => setIsRegistrationOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                Register My Trademark <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" onClick={() => window.openLiveChat && window.openLiveChat()} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                <MessageCircle className="mr-2 h-4 w-4" /> Talk to an Expert Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What is Trademark Registration?</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base">
                <p>
                  Trademark registration is the legal process of registering your brand name, logo, slogan, or other identifying marks with the United States Patent and Trademark Office (USPTO). This registration grants you exclusive rights to use that mark in connection with your goods or services throughout the United States.
                </p>
                <p>
                  A trademark serves as a badge of origin, identifying your products or services and distinguishing them from those of competitors. Registration provides legal protection against unauthorized use and helps build brand recognition and consumer trust.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1626148750586-df6e1b0bebf2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBjb25zdWx0YXRpb258ZW58MHx8fGJsdWV8MTc2Mjg4NzExM3ww&ixlib=rb-4.1.0&q=85" 
                alt="Professional office consultation and business services"
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Key Benefits</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Protect your brand and gain valuable legal rights
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection onOpenRegistration={(tmSignUpType, packageTitle, packageAmount) => {
        setTmSignUpType(tmSignUpType);
        setSelectedPackage({ title: packageTitle, amount: packageAmount });
        setIsRegistrationOpen(true);
      }} />

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-gray-50 border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Protect your brand today with our professional trademark registration services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setIsRegistrationOpen(true)} className="bg-white text-orange-600 hover:bg-gray-100">
              Register My Trademark <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" onClick={() => window.openLiveChat && window.openLiveChat()} variant="outline" className="border-white text-white hover:bg-white/10">
              <MessageCircle className="mr-2 h-4 w-4" /> Talk to an Expert Now
            </Button>
          </div>
        </div>
      </section>

      {/* Chat Popup */}
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
      <RegistrationPopup isOpen={isRegistrationOpen} onClose={() => setIsRegistrationOpen(false)} tmSignUpType={tmSignUpType} packageTitle={selectedPackage.title} packageAmount={selectedPackage.amount} />
    </div>
  );
};

export default TrademarkRegistration;
